import type { RouteOptions } from 'js/core_pre_webpack/routes';
import routes from 'js/core_pre_webpack/routes';
import { trackNavClicked } from 'js/research_radar/tracking';

import { FeatureFlags } from './types';

/**
 * Registry of *experimental* nav items for the logged-in Focused Nav.
 *
 * This is the single, declarative place a team slots a new nav item into the
 * normal nav to test a feature. Add one entry here and the item shows up in the
 * desktop nav and (by default) the mobile bottom tab bar, gated by your feature
 * flag — no per-item edits scattered through `FocusedNavView`.
 *
 * How a new team adds an item:
 *   1. Add a boolean to `FeatureFlags` in ./types.ts.
 *   2. Add an eligibility class mirroring app/lib/research_radar/feature_access.rb
 *      (admin permission override -> Redis flag -> AbTesting variant).
 *   3. Add the A/B test YAML under config/ab_tests/ (model on
 *      config/ab_tests/bulk_download_2026_q1_research_radar.yml).
 *   4. Wire the flag into `feature_flag_props` in
 *      app/controllers/api/v0/focused_nav_controller.rb.
 *   5. Add an entry to EXPERIMENTAL_NAV_ITEMS below.
 *   6. Decide what your eligibility class does about the nav_impact_stats_2026_q3_v1
 *      whole-bar arms. `FocusedNavView` returns the `ImpactNavBar` tree early for those
 *      arms and passes it no `enabledExperimentalItems`, so your item does not render for
 *      them at any flag value. If your gate buckets an A/B test, it must refuse those arms
 *      *before* bucketing (see `Citeright::NavbarAccess#enabled?` and
 *      `ImpactStats::NavTest.whole_bar?`) — a bucketing row for an arm that cannot show the
 *      item is undetectable at readout.
 *
 * An item is either ADDITIVE (no `replaces`) or it TAKES OVER a core slot
 * (`replaces`). See the field docs below.
 */

/** Core nav slots an experimental item may take over. Never 'tools' (the menu host). */
export type CoreSlot = 'home' | 'analytics' | 'mentions' | 'upload';

type NavRoute = (opts?: RouteOptions) => string;

export type ExperimentalNavItem = {
  /** Stable key — React key + analytics id. e.g. 'research_radar'. */
  key: string;
  /** Which FeatureFlags boolean gates this item. `keyof` keeps registry + backend in sync. */
  flag: keyof FeatureFlags;
  label: string;
  /** Material Symbols icon name passed to <Icon name=...>. */
  iconName: string;
  /** Route helper, called with { from_navbar: true, trigger: 'nav' }. */
  route: NavRoute;
  /**
   * OPTIONAL. If set, this item takes over that core slot (desktop: inherits the
   * slot's position/priority; mobile: swaps it in the bottom bar if that core item
   * is present, else appends as the 5th tab). If omitted, the item is purely
   * additive (appended on desktop at `priority`, and added to the mobile bar as the
   * 5th item when `showInMobile`).
   */
  replaces?: CoreSlot;
  /**
   * Mobile bottom-tab visibility. Defaults to TRUE (shown). Set false to make the
   * item desktop-only.
   */
  showInMobile?: boolean;
  /**
   * Desktop responsive-trim priority. HIGHER numbers trim FIRST: `FocusedNavView` keeps
   * icons with `priority <= maxLevel` and maxLevel falls as the viewport narrows, so a low
   * number survives longest. (`Tools` uses -Infinity to be untrimmable.)
   */
  priority: number;
  /** Only render for premium users. Default false. */
  premiumOnly?: boolean;
  /** Show the green "new" dot. Default true (this is an experiment surface). */
  showNewDot?: boolean;
  /** ImpressionWatcher source. Default `${key}.nav`. */
  impressionSource?: string;
  /**
   * Optional unread-count badge shown when count > 0. `dotColor` defaults to 'red'
   * (the standard NotificationDot); use 'green' for features whose unread indicator
   * is the green dot (e.g. Research Radar).
   */
  unreadCount?: {
    route: NavRoute;
    countField: string;
    dotColor?: 'red' | 'green';
  };
  /**
   * Optional per-surface click tracking. Called with 'desktop' | 'mobile' on click.
   * Lets a feature keep its bespoke analytics without a custom component.
   */
  onNavClick?: (source: 'desktop' | 'mobile') => void;
};

export const EXPERIMENTAL_NAV_ITEMS: ExperimentalNavItem[] = [
  // Additive reference example (no `replaces`): appears on desktop at priority 2 and
  // as the 5th mobile bottom-tab item, premium-only, with an unread dot.
  {
    key: 'research_radar',
    flag: 'showResearchRadarNav',
    label: 'Radar',
    iconName: 'sensors',
    route: routes.research_radar_url,
    // showInMobile defaults true -> appears as the 5th mobile tab, matching today.
    priority: 2,
    premiumOnly: true,
    // Radar already shows a green dot for unread results; avoid two greens.
    showNewDot: false,
    impressionSource: 'research_radar.nav',
    unreadCount: {
      route: routes.api_v0_research_radar_unread_count_path,
      countField: 'unread_count',
      dotColor: 'green',
    },
    onNavClick: trackNavClicked,
  },
  // Additive: appears alongside Upload for users who ran an academic STEM manual CiteRight
  // report in the last month (gated by Citeright::NavbarAccess -> per-user Redis flag ->
  // A/B test). This cohort already got value out of CiteRight, so the point is to give
  // them a way back to it — not to take a tool away from them, which is what the earlier
  // `replaces: 'upload'` version did.
  //
  // showInMobile is set explicitly because it defaults to TRUE: without it this item would
  // now be appended as a 5th mobile bottom tab. The test targets the desktop nav only.
  //
  // priority 2, not 3: trimming keeps icons with `priority <= maxLevel` and maxLevel falls
  // as the viewport narrows, so a HIGHER number is trimmed sooner. At 3 this would tie with
  // the Upload slot and the two would vanish together; 2 matches research_radar and keeps
  // CiteRight past Home (5) and Upload (3) on narrow desktop widths.
  //
  // Not rendered on the nav_impact_stats_2026_q3_v1 whole-bar arms — ImpactNavBar takes no
  // experimental items — so Citeright::NavbarAccess refuses those arms rather than bucketing
  // users who could never see this icon. FocusedNavView.test.tsx pins that rendering fact.
  {
    key: 'citeright',
    flag: 'showCiterightNavbar',
    label: 'CiteRight',
    iconName: 'fact_check',
    route: (opts) => routes.citeright_index_url({ ...opts, source: 'navbar' }),
    showInMobile: false,
    priority: 2,
    showNewDot: false,
  },
];
